/**
 * Contact Component for 2D Imaging, Inc. Website
 * Allows customization of heading and subtitle
 */

class Contact extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Get attributes or use defaults
    const heading = this.getAttribute('heading') || 'Contact Us';
    const subtitle = this.getAttribute('subtitle') || 'We\'re here to help with all your ultrasound equipment needs.';

    this.innerHTML = `
    <!-- Contact Section -->
    <section id="contact" class="contact section">
        <div class="container">
            <div class="section__title">
                <h2>${heading}</h2>
                <p>${subtitle}</p>
            </div>
            <div class="contact-content">
                <div class="contact-info animate">
                    <h3>Get In Touch</h3>
                    <div class="contact-item">
                        <div class="contact-icon"><i class="fas fa-phone-alt"></i></div>
                        <div>
                            <h4>Phone</h4>
                            <p><span class="protected-phone">(714) 524-5888</span></p>
                        </div>
                    </div>
                    <div class="contact-item">
                        <div class="contact-icon"><i class="fas fa-envelope"></i></div>
                        <div>
                            <h4>Email</h4>
                            <p><a href="#" class="protected-email" data-username="support" data-domain="2dimaging.com">Contact Us</a></p>
                        </div>
                    </div>
                    <div class="contact-item">
                        <div class="contact-icon"><i class="fas fa-clock"></i></div>
                        <div>
                            <h4>Business Hours</h4>
                            <p>Monday - Friday, 8:30 AM - 4:30 PM PST</p>
                        </div>
                    </div>
                    <div class="contact-item">
                        <div class="contact-icon"><i class="fas fa-map-marker-alt"></i></div>
                        <div>
                            <h4>Location</h4>
                            <p>Placentia, California</p>
                        </div>
                    </div>
                </div>
                <div class="form animate" id="contact-form-container">
                    <form id="contact-form" name="contact" method="POST" enctype="multipart/form-data" action="${window.FORM_ENDPOINT}/message-sent.html">
                        <input type="hidden" name="form-name" value="contact">
                        <input type="hidden" name="subject">
                        <p class="form__honeypot" hidden>
                            <label>Leave this field empty <input name="bot-field" tabindex="-1" autocomplete="off"></label>
                        </p>
                        <div class="form__group">
                            <input type="text" name="name" class="form__input" placeholder="Name" autocomplete="name" required>
                        </div>
                        <div class="form__group">
                            <input type="text" name="company" class="form__input" placeholder="Company Name (Optional)" autocomplete="organization">
                        </div>
                        <div class="form__group">
                            <input type="email" name="email" class="form__input" placeholder="Email Address" autocomplete="email" required>
                        </div>
                        <div class="form__group">
                            <input type="tel" name="phone" class="form__input" placeholder="Phone Number (Optional)" autocomplete="tel">
                        </div>
                        <div class="form__group">
                            <select name="inquiry" class="form__input" required>
                                <option value="" disabled selected>Inquiry Type</option>
                                <option>Repair/Evaluation Quote</option>
                                <option>Probe/System Quote</option>
                                <option>On-Site Service Request</option>
                                <option>Preventative Maintenance</option>
                                <option>Careers</option>
                                <option>General Inquiry</option>
                                <option>Other</option>
                            </select>
                        </div>
                        <div class="form__careers" hidden>
                            <div class="form__group">
                                <select name="position" class="form__input" disabled>
                                    <option value="" disabled selected>Position Applying For</option>
                                    <option>Probe Repair Technician I (Entry level)</option>
                                    <option>Probe Repair Technician II (Intermediate)</option>
                                    <option>Probe Repair Technician III (Senior)</option>
                                    <option>Not sure which level fits</option>
                                </select>
                            </div>
                            <div class="form__group">
                                <label class="form__file-label">Attach your resume (PDF or Word, optional)
                                    <input type="file" name="resume" class="form__input form__input--file" accept=".pdf,.doc,.docx" disabled>
                                </label>
                            </div>
                        </div>
                        <div class="form__group">
                            <textarea name="message" class="form__input form__input--textarea" placeholder="Let us know how we can assist you. Please include any relevant equipment models or details."></textarea>
                        </div>
                        <button type="submit" class="form__submit">Send Message</button>
                        <p class="form__status" role="alert"></p>
                        <p style="margin-top: 15px; font-size: 14px; color: #666; font-family: var(--subtitle-font); font-weight: 400;">We typically respond within 1 business day. For urgent service requests, please call us directly at (714) 524-5888.</p>
                    </form>
                </div>
            </div>
        </div>
    </section>
    `;
    
    // Execute the script immediately after the component is connected
    this.initializeProtection();

    // Position and resume fields only apply to career inquiries; disabled fields stay out of the submission
    const inquiry = this.querySelector('select[name="inquiry"]');
    const careers = this.querySelector('.form__careers');
    const position = careers.querySelector('select[name="position"]');
    const resume = careers.querySelector('input[name="resume"]');
    inquiry.addEventListener('change', () => {
      const isCareers = inquiry.value === 'Careers';
      careers.hidden = !isCareers;
      position.disabled = resume.disabled = !isCareers;
      position.required = isCareers;
    });
  }
  
  // Method to set up the email and phone protection
  initializeProtection() {
    // Email protection - Assemble email on click
    const protectedEmails = this.querySelectorAll('.protected-email');
    protectedEmails.forEach(function(element) {
        element.addEventListener('click', function(e) {
            e.preventDefault();
            const username = this.getAttribute('data-username');
            const domain = this.getAttribute('data-domain');
            window.location.href = 'mailto:' + username + '@' + domain;
        });
        
        // Show email on hover for better UX
        element.addEventListener('mouseover', function() {
            const username = this.getAttribute('data-username');
            const domain = this.getAttribute('data-domain');
            this.textContent = username + '@' + domain;
        });
        
        element.addEventListener('mouseout', function() {
            this.textContent = 'Contact Us';
        });
    });
    
    // Phone protection - Format and make clickable on interaction
    const protectedPhones = this.querySelectorAll('.protected-phone');
    protectedPhones.forEach(function(element) {
        const phoneText = element.textContent;
        
        // Make clickable
        element.parentElement.addEventListener('click', function() {
            window.location.href = 'tel:' + phoneText.replace(/[^0-9]/g, '');
        });
        
        // Style to indicate clickable
        element.parentElement.style.cursor = 'pointer';
    });
  }
}

customElements.define("site-contact", Contact); 